import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import { pathFor, getPageById } from "@/data/pages";

// PwC newsroom-style hero — the cream box is a FLOATING block that sits ON
// TOP of the image. Image hugs the RIGHT viewport edge and is taller than the
// cream box. White space surrounds the cream box (top/bottom and left gutter).
export default function PageHero({ pageId, mainTitle, subtitle, image }) {
  const { lang, t } = useLang();
  const page = getPageById(pageId);

  const trail = [{ label: t.pageNames.home, href: pathFor("home", lang) }];
  if (page?.group) trail.push({ label: t.groupLabels[page.group], href: null });
  trail.push({ label: t.pageNames[pageId], href: null });

  return (
    <section data-testid={`page-hero-${pageId}`} className="bg-white">
      {/* Breadcrumbs */}
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 pt-4 pb-2 md:pb-1">
        <nav
          aria-label="Breadcrumb"
          data-testid={`breadcrumbs-${pageId}`}
          className="flex items-center gap-2 text-[13.5px] flex-wrap"
        >
          {trail.map((node, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              {node.href ? (
                <Link to={node.href} className="text-[var(--abs-ink)] hover:text-[var(--abs-orange)] underline-offset-4 hover:underline font-medium">
                  {node.label}
                </Link>
              ) : (
                <span className={i === trail.length - 1 ? "text-[var(--abs-ink)] font-medium" : "text-[var(--abs-mute)]"}>{node.label}</span>
              )}
              {i < trail.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-[var(--abs-orange)]" />}
            </span>
          ))}
        </nav>
      </div>

      {/* ----- Mobile layout (stacked) ----- */}
      <div className="md:hidden">
        <div className="bg-[var(--abs-cream)] px-6 py-10 pb-12">
          <h1 className="serif text-[var(--abs-ink)] text-[34px] sm:text-[40px] leading-[1.08] tracking-tight">
            {mainTitle}
          </h1>
          {subtitle && (
            <p className="mt-5 text-[14.5px] leading-[1.6] text-[var(--abs-ink)]">
              {subtitle}
            </p>
          )}
        </div>
        <div className="relative w-full h-[260px] sm:h-[320px] bg-[var(--abs-soft)] overflow-hidden">
          <img src={image} alt={mainTitle} className="w-full h-full object-cover" />
          <div
            className="absolute pointer-events-none"
            style={{
              left: "-2%",
              right: "8%",
              top: "32%",
              height: "30%",
              background: "var(--abs-orange)",
              transform: "skewY(-8deg)",
            }}
            aria-hidden
          />
        </div>
      </div>

      {/* ----- Desktop layout (floating cream box on top of image) ----- */}
      <div className="hidden md:block relative pb-10 lg:pb-14" data-testid={`hero-split-${pageId}`}>
        {/* Image — right edge to viewport edge */}
        <div
          className="absolute right-0 overflow-hidden bg-[var(--abs-soft)]"
          style={{ top: "16px", height: "560px", left: "36%" }}
        >
          <img src={image} alt={mainTitle} className="absolute inset-0 w-full h-full object-cover" />
          {/* Orange parallelogram on top of image (lower-right area) */}
          <div
            className="absolute pointer-events-none"
            style={{
              right: "0%",
              top: "44%",
              width: "68%",
              height: "28%",
              background: "var(--abs-orange)",
              transform: "skewY(-7deg)",
            }}
            aria-hidden
          />
        </div>

        {/* Cream box — floating, slightly closer to the left edge */}
        <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-6 relative">
          <div
            className="relative bg-[var(--abs-cream)] z-[2]"
            style={{ width: "min(640px, 58%)", marginTop: "40px", marginBottom: "32px", minHeight: "440px" }}
          >
            <div className="px-10 lg:px-14 py-14 lg:py-16 h-full flex flex-col justify-center">
              <h1 className="serif text-[var(--abs-ink)] text-5xl lg:text-[64px] leading-[1.04] tracking-tight">
                {mainTitle}
              </h1>
              {subtitle && (
                <p className="mt-7 text-[16px] leading-[1.6] text-[var(--abs-ink)] max-w-md">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
